<div class="min-vh-100 bg-light">
  <app-header [page]="'Wallets'"></app-header>

  <main class="container py-4">
    <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3" *ngIf="!loading && walletData">
      <div>
        <h4 class="mb-0 fw-bold">Deposit</h4>
        <div class="text-muted small">Choose how you want to fund your {{ currencyCode }} wallet</div>
      </div>

      <div class="d-flex gap-2">
        <a class="btn btn-outline-secondary btn-sm" routerLink="/wallets/manage/{{ walletData.walletId }}">Back</a>
      </div>
    </div>

    <div class="alert alert-info" *ngIf="loading">
      <div class="d-flex align-items-center">
        <div class="spinner-border spinner-border-sm me-2"></div>
        Processing your deposit request...
      </div>
    </div>

    <div *ngIf="error" class="alert alert-danger">{{ error }}</div>

    <div class="alert alert-danger border-danger shadow-sm mb-4" *ngIf="depositKycRequired">
      <div class="d-flex">
        <div class="me-3 fs-2">⚠️</div>
        <div class="flex-grow-1">
          <h5 class="fw-bold mb-2">Deposits are unavailable</h5>
          <p class="mb-2">
            You have not completed KYC verification for
            <strong>{{ depositJurisdiction }}</strong>.
          </p>
          <p class="text-muted mb-3">
            Deposits into this wallet are disabled until your compliance application has been approved.
          </p>
          <ol>
            <li class="mb-2" *ngIf="shouldShowSwitchJurisdictionStep">
              Switch your account jurisdiction to <strong>{{ depositJurisdiction }}</strong> in your account settings.
            </li>
            <li class="mb-2">
              Complete your KYC verification for <strong>{{ depositJurisdiction }}</strong>.
            </li>
            <li class="mb-2">
              Submit a compliance application for <strong>{{ depositJurisdiction }}</strong>.
            </li>
            <li>After your verification is approved, you can continue to deposit into this wallet.</li>
          </ol>
          <button class="btn btn-danger" type="button" (click)="goToCompliance()">
            Complete KYC Verification
          </button>
        </div>
      </div>
    </div>

    <div *ngIf="loading && !walletData" class="card shadow-sm border-0">
      <div class="card-body p-4">
        <div class="placeholder-glow">
          <div class="placeholder col-4 mb-2"></div>
          <div class="placeholder col-7 mb-4"></div>
          <div class="placeholder col-12 mb-2"></div>
          <div class="placeholder col-10 mb-2"></div>
          <div class="placeholder col-8"></div>
        </div>
      </div>
    </div>

    <div class="alert alert-danger d-flex align-items-start gap-2" *ngIf="pageBlocker">
      <div>
        <div class="fw-bold">Deposit temporarily unavailable</div>
        <div>{{ pageBlocker }}</div>
        <div class="small mt-1 text-muted" *ngIf="pageBlockerCode">Code: {{ pageBlockerCode }}</div>
      </div>
    </div>

    <div class="row g-3" *ngIf="!loading && walletData">
      <div class="col-12 col-lg-7">
        <div *ngIf="error && error.toLowerCase().includes('kyc')" class="alert alert-warning">
          {{ error }}
          <div class="mt-2">
            <a routerLink="/compliance" class="btn btn-sm btn-outline-primary">Complete Verification</a>
          </div>
        </div>

        <form class="card shadow-sm border-0" [formGroup]="dataForm" (ngSubmit)="continue()">
          <div class="card-body p-4">
            <div class="fw-bold mb-1">Deposit method</div>
            <div class="text-muted small mb-3">
              Available methods are based on this wallet, your country and the providers currently enabled by SwipFx.
            </div>

            <div class="text-muted small mb-2" *ngIf="gatewayLoading">
              <span class="spinner-border spinner-border-sm me-2"></span>
              Loading deposit methods...
            </div>

            <div class="alert alert-warning small" *ngIf="!gatewayLoading && !hasDepositChannels">
              No deposit method is currently configured for this wallet.
            </div>

            <div class="row g-2 mb-4" *ngIf="hasDepositChannels">
              <div class="col-12 col-md-6" *ngFor="let channel of depositChannels">
                <button
                  type="button"
                  class="deposit-channel-card w-100 text-start"
                  [class.selected]="selectedDepositChannelId === channel.mappingId"
                  [disabled]="!!pageBlocker"
                  (click)="selectDepositChannel(channel)">
                  <div class="d-flex justify-content-between gap-3">
                    <div>
                      <div class="fw-semibold">{{ channel.displayName }}</div>
                      <div class="small text-muted mt-1" *ngIf="channel.description">{{ channel.description }}</div>
                      <div class="small text-muted mt-1" *ngIf="!channel.description">
                        {{ channel.channelType === 'virtual-account' ? 'Transfer directly to your dedicated account.' :
                           channel.channelType === 'instructions' ? 'Follow the transfer instructions shown after selection.' :
                           'Complete your payment securely through the payment provider.' }}
                      </div>
                    </div>
                    <span class="badge text-bg-primary-subtle border border-primary-subtle text-primary align-self-start"
                      *ngIf="channel.isDefault">
                      Recommended
                    </span>
                  </div>
                  <div class="d-flex align-items-center justify-content-between mt-3 small">
                    <span class="text-muted">{{ channel.gateway.name || channel.gateway.code || 'Payment provider' }}</span>
                    <span class="fw-semibold text-primary" *ngIf="selectedDepositChannelId === channel.mappingId">Selected</span>
                  </div>
                </button>
              </div>
            </div>

            <ng-container *ngIf="selectedChannel">
              <div class="mb-3" *ngIf="isOnlinePaymentChannel">
                <label class="form-label">Amount</label>
                <input
                  class="form-control"
                  type="number"
                  min="1"
                  inputmode="decimal"
                  formControlName="amount"
                  [readonly]="!!pageBlocker"
                  [class.is-invalid]="(submitted || amount?.touched) && (amount?.invalid || !channelAmountEligible)"
                  placeholder="0.00" />

                <div class="invalid-feedback" *ngIf="(submitted || amount?.touched) && amount?.errors">
                  <div *ngIf="amount?.errors?.['required']">Amount is required.</div>
                  <div *ngIf="amount?.errors?.['min']">
                    Minimum deposit is {{ walletData.currencyRules.minDeposit || 0 | smartAmount:walletData.currency.symbol }}.
                  </div>
                  <div *ngIf="amount?.errors?.['max']">
                    Maximum allowed deposit is {{ limit || 0 | smartAmount:walletData.currency.symbol }}.
                  </div>
                </div>

                <div class="text-danger small mt-1" *ngIf="amount?.valid && !channelAmountEligible">
                  {{ channelAmountMessage }}
                </div>

                <div class="form-text">You’ll be credited after the provider confirms the payment.</div>
              </div>

              <div class="alert alert-info small" *ngIf="isOnlinePaymentChannel && requiresDepositPin">
                <div class="fw-semibold mb-1">PIN authorization enabled</div>
                You will be asked for your six-digit account PIN before leaving SwipFx for the payment provider.
              </div>

              <div class="alert alert-warning small" *ngIf="isInteracChannel">
                <div class="fw-bold mb-1">{{ selectedChannel.displayName }}</div>
                <div>
                  Register the email address you will send the Interac e-Transfer from. This allows SwipFx to match the incoming payment to your wallet.
                </div>

                <div class="input-group mt-3">
                  <input
                    class="form-control"
                    type="email"
                    [(ngModel)]="interacEmail"
                    [ngModelOptions]="{ standalone: true }"
                    (ngModelChange)="onInteracEmailChanged()"
                    placeholder="your-bank-email@example.com" />

                  <button class="btn btn-outline-primary" type="button" [disabled]="gatewayActionLoading" (click)="saveInteracEmail()">
                    <span *ngIf="gatewayActionLoading" class="spinner-border spinner-border-sm me-2"></span>
                    Save
                  </button>
                </div>

                <div class="form-text" *ngIf="!interacEmailReady">
                  Save the sending email to reveal the Interac transfer destination.
                </div>

                <div class="mt-3" *ngIf="cadInstructions">
                  <div class="text-muted">Send Interac e-Transfer to:</div>
                  <div class="d-flex align-items-center justify-content-between bg-white border rounded p-2 mt-1">
                    <div class="fw-semibold">{{ cadInstructions.interacEmail }}</div>
                    <button class="btn btn-sm btn-outline-secondary" type="button" (click)="copyText(cadInstructions.interacEmail)">
                      Copy
                    </button>
                  </div>
                  <div class="mt-2">{{ cadInstructions.message }}</div>
                  <div class="mt-2 small">
                    Send the amount directly from your bank. You do not need to create a separate deposit request on SwipFx.
                  </div>
                </div>
              </div>

              <div class="alert alert-info small" *ngIf="isVirtualAccountChannel">
                <div class="d-flex justify-content-between align-items-start gap-2">
                  <div>
                    <div class="fw-bold mb-1">{{ selectedChannel.displayName }}</div>
                    <div *ngIf="!virtualAccount">
                      Create your reusable {{ currencyCode }} transfer account. Once it is created, you can fund this wallet by bank transfer whenever you need to.
                    </div>
                    <div *ngIf="virtualAccount">
                      Status:
                      <span class="badge" [ngClass]="{
                        'bg-success': isVirtualAccountReady(virtualAccount),
                        'bg-warning text-dark': virtualAccount.status === 'PENDING',
                        'bg-danger': virtualAccount.status === 'FAILED' || virtualAccount.status === 'REJECTED'
                      }">
                        {{ virtualAccount.status }}
                      </span>
                    </div>
                  </div>

                  <div class="d-flex gap-2">
                    <button
                      class="btn btn-sm btn-outline-primary"
                      type="button"
                      *ngIf="!virtualAccount"
                      [disabled]="gatewayActionLoading"
                      (click)="createVirtualAccount()">
                      <span *ngIf="gatewayActionLoading" class="spinner-border spinner-border-sm me-1"></span>
                      Create Account
                    </button>

                    <button
                      class="btn btn-sm btn-outline-secondary"
                      type="button"
                      *ngIf="virtualAccount"
                      [disabled]="refreshingVirtualAccount"
                      (click)="refreshVirtualAccount()">
                      <span *ngIf="refreshingVirtualAccount" class="spinner-border spinner-border-sm me-1"></span>
                      Refresh
                    </button>
                  </div>
                </div>

                <div class="mt-3" *ngIf="virtualAccount">
                  <div class="row g-2">
                    <div class="col-md-6" *ngIf="virtualAccount.accountName">
                      <div class="text-muted">Account Name</div>
                      <div class="fw-semibold">{{ virtualAccount.accountName }}</div>
                    </div>

                    <div class="col-md-6" *ngIf="virtualAccount.accountNumber">
                      <div class="text-muted">Account Number</div>
                      <div class="d-flex justify-content-between align-items-center bg-white border rounded p-2">
                        <span class="fw-semibold">{{ virtualAccount.accountNumber }}</span>
                        <button class="btn btn-sm btn-outline-secondary" type="button" (click)="copyText(virtualAccount.accountNumber)">Copy</button>
                      </div>
                    </div>

                    <div class="col-md-6" *ngIf="virtualAccount.bankName">
                      <div class="text-muted">Bank Name</div>
                      <div class="fw-semibold">{{ virtualAccount.bankName }}</div>
                    </div>

                    <div class="col-md-6" *ngIf="virtualAccount.bankCode">
                      <div class="text-muted">Bank Code</div>
                      <div class="fw-semibold">{{ virtualAccount.bankCode }}</div>
                    </div>

                    <div class="col-md-6" *ngIf="virtualAccount.sortCode">
                      <div class="text-muted">Sort Code</div>
                      <div class="fw-semibold">{{ virtualAccount.sortCode }}</div>
                    </div>

                    <div class="col-md-6" *ngIf="virtualAccount.iban">
                      <div class="text-muted">IBAN</div>
                      <div class="fw-semibold text-break">{{ virtualAccount.iban }}</div>
                    </div>

                    <div class="col-md-6" *ngIf="virtualAccount.accountReference">
                      <div class="text-muted">Reference</div>
                      <div class="d-flex justify-content-between align-items-center bg-white border rounded p-2">
                        <span class="fw-semibold text-break">{{ virtualAccount.accountReference }}</span>
                        <button class="btn btn-sm btn-outline-secondary" type="button" (click)="copyText(virtualAccount.accountReference)">Copy</button>
                      </div>
                    </div>

                    <div class="col-md-6">
                      <div class="text-muted">Provider</div>
                      <div class="fw-semibold">{{ selectedChannel.gateway.name || virtualAccount.provider || virtualAccount.providerCode }}</div>
                    </div>
                  </div>

                  <div class="alert alert-warning mt-3 mb-0" *ngIf="!isVirtualAccountReady(virtualAccount)">
                    Your transfer account is still being prepared. SwipFx will refresh its status automatically.
                  </div>

                  <div class="mt-3 small" *ngIf="isVirtualAccountReady(virtualAccount)">
                    Transfer funds directly to these details. Incoming transfers are matched to this {{ currencyCode }} wallet automatically after provider verification.
                  </div>
                </div>
              </div>

              <button
                class="btn btn-primary w-100 mt-4"
                type="submit"
                *ngIf="isOnlinePaymentChannel"
                [disabled]="!canStartOnlineDeposit">
                <span *ngIf="loading && walletData" class="spinner-border spinner-border-sm me-2"></span>
                {{ loading && walletData ? 'Processing...' : 'Continue to Payment' }}
              </button>
            </ng-container>
          </div>
        </form>

        <div class="card shadow-sm border-0 mt-3">
          <div class="card-body p-4">
            <div class="d-flex align-items-center justify-content-between">
              <div class="fw-bold">Deposit Limits</div>
            </div>

            <hr class="my-3">
            <div class="d-flex justify-content-between mb-2">
              <div class="text-muted small">Transaction Limit</div>
              <div class="fw-semibold">
                {{ getLimitData(walletData.tierInfo, 'Deposit', 'perTransactionLimit') || 0 | smartAmount:walletData.currency.symbol }}
              </div>
            </div>

            <div class="d-flex justify-content-between mb-2" *ngIf="walletData.currencyRules.usage.hasApprovedKyc">
              <div class="text-muted small">Daily Limit</div>
              <div class="fw-semibold">
                {{ getLimitData(walletData.tierInfo, 'Deposit', 'remainingDailyLimit') || 0 | smartAmount:walletData.currency.symbol }} /
                {{ getLimitData(walletData.tierInfo, 'Deposit', 'dailyLimit') || 0 | smartAmount:walletData.currency.symbol }}
              </div>
            </div>

            <div class="d-flex justify-content-between mb-2" *ngIf="walletData.currencyRules.usage.hasApprovedKyc">
              <div class="text-muted small">Monthly Limit</div>
              <div class="fw-semibold">
                {{ getLimitData(walletData.tierInfo, 'Deposit', 'remainingMonthlyLimit') || 0 | smartAmount:walletData.currency.symbol }} /
                {{ getLimitData(walletData.tierInfo, 'Deposit', 'monthlyLimit') || 0 | smartAmount:walletData.currency.symbol }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 col-lg-5">
        <div class="card shadow-sm border-0">
          <div class="card-body p-4">
            <div class="d-flex align-items-center justify-content-between">
              <div class="fw-bold">Summary</div>
              <span class="badge text-bg-primary-subtle border border-primary-subtle text-primary">Preview</span>
            </div>

            <hr class="my-3">

            <div class="d-flex justify-content-between mb-2">
              <div class="text-muted small">Wallet</div>
              <div class="fw-semibold">{{ walletData.currency.name }} ({{ walletData.currency.code }})</div>
            </div>

            <div class="d-flex justify-content-between mb-2">
              <div class="text-muted small">Current Balance</div>
              <div class="fw-semibold">{{ walletData.balance | smartAmount:walletData.currency.symbol }}</div>
            </div>

            <div class="d-flex justify-content-between mb-2">
              <div class="text-muted small">Available Balance</div>
              <div class="fw-semibold">{{ walletData.availableBalance | smartAmount:walletData.currency.symbol }}</div>
            </div>

            <div class="d-flex justify-content-between mb-2" *ngIf="isOnlinePaymentChannel">
              <div class="text-muted small">Amount</div>
              <div class="fw-semibold">{{ amount?.value || 0 | smartAmount:walletData.currency.symbol }}</div>
            </div>

            <div class="d-flex justify-content-between mb-2">
              <div class="text-muted small">Method</div>
              <div class="fw-semibold text-end">{{ selectedChannel?.displayName || 'Select a method' }}</div>
            </div>

            <div class="d-flex justify-content-between mb-2" *ngIf="selectedChannel">
              <div class="text-muted small">Provider</div>
              <div class="fw-semibold text-end">{{ selectedChannel.gateway.name || selectedChannel.gateway.code || '-' }}</div>
            </div>

            <div class="mt-3 alert alert-warning small mb-0" *ngIf="isOnlinePaymentChannel">
              You will be redirected to the selected payment provider to complete your deposit.
            </div>

            <div class="mt-3 alert alert-info small mb-0" *ngIf="usesManualDeposit">
              The amount credited will be the amount confirmed by the provider after your transfer is received and verified.
            </div>

            <div class="mt-3 alert alert-info small mb-0">
              Tip: use payment details that belong to you so compliance checks and payment matching are not delayed.
            </div>
          </div>
        </div>

        <div class="text-muted small mt-3">
          Need help? Contact support and include the wallet currency and payment method you used.
        </div>
      </div>

      <app-support-context-card
        type="deposit"
        title="Need help with deposits?"
        subtitle="Get quick answers about deposit status, pending confirmations, and payment issues.">
      </app-support-context-card>
    </div>

    <app-footer></app-footer>
  </main>

  <app-account-pin-pad
    [modal]="true"
    [(visible)]="showDepositPinModal"
    [(pin)]="depositPin"
    title="Authorize deposit"
    description="Enter your six-digit account PIN to commit this deposit."
    confirmLabel="Continue to Payment"
    [loading]="loading"
    [error]="depositPinError"
    (pinSubmit)="authorizeDeposit($event)"
    (cancelled)="closeDepositPinModal()">
  </app-account-pin-pad>
</div>
